// =============================================================================
// adaptColor - keep user-chosen colours readable in both themes.
//
// Appointment type colours, practice accents and similar values are DATA: a
// practice picks them and they live in the database. They cannot be replaced
// with a semantic token. But a mid-tone chosen against a white background can
// fall to 2:1 on a dark surface, so it has to be adapted at render time.
//
// Approach: keep the hue, lift or drop the lightness until the contrast against
// the current surface clears the WCAG AA threshold. Hue is preserved because the
// colour carries meaning to the user - "the blue one is a new patient".
// =============================================================================
export function hexToRgb(hex) {
  let h = String(hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
export const toHex = ({ r, g, b }) => "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");

function relLum({ r, g, b }) {
  const f = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
export const contrast = (a, b) => {
  const la = relLum(a), lb = relLum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
export const mix = (c, target, t) => ({ r: c.r + (target.r - c.r) * t, g: c.g + (target.g - c.g) * t, b: c.b + (target.b - c.b) * t });

const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 0, g: 0, b: 0 };

export function isDarkTheme() {
  if (typeof document === "undefined") return false;
  return document.documentElement.getAttribute("data-theme") === "dark";
}

// Adapt a foreground colour so it clears `min` contrast against the surface.
export function adaptColor(hex, { min = 4.5, surface = null } = {}) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const dark = isDarkTheme();
  const bg = hexToRgb(surface) || (dark ? { r: 28, g: 31, b: 30 } : WHITE);
  if (contrast(c, bg) >= min) return hex;
  const towards = dark ? WHITE : BLACK;   // lift on dark, deepen on light
  let best = c;
  for (let t = 0.05; t <= 0.95; t += 0.05) {
    const cand = mix(c, towards, t);
    best = cand;
    if (contrast(cand, bg) >= min) break;
  }
  return toHex(best);
}

// Tinted surface derived from a user colour (badge background).
export function adaptSurface(hex, alpha = 0.12) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  // On dark, a light wash reads as glare; blend toward the panel instead.
  return isDarkTheme()
    ? "rgba(" + c.r + "," + c.g + "," + c.b + "," + Math.min(alpha * 1.6, 0.28) + ")"
    : "rgba(" + c.r + "," + c.g + "," + c.b + "," + alpha + ")";
}
